import { Override, IBinding } from "./types";

export enum CustomizationSelectionType {
    PRINTED_IMAGE = "printed-image",
    PRINTED_TEXT = "printed-text",
    COLOR_PICKER = "color-picker",
    COLOR_SWATCH = "color-swatch",
    SELECTABLE = "selectable",
}

export enum MetaSelectionType {
    TEXT = "meta-text",
    SELECTABLE = "meta-selectable",
}

export enum PrintedTextOptionValueKey {
    FONT = "font",
    FONT_SIZE = "fontSize",
    COLOR_PICKER = "colorPicker",
    COLOR_SWATCH = "colorSwatch",
}

export type TCustomizationAttributeId = string;
export type TCustomizationImageId = string;
export type TCustomizationImageUrl = string;
export type TSelectableValueId = string;
export type TRGBHex = string;

export class CustomizationSelectionMap extends Map<TCustomizationAttributeId, TCustomizationSelection> {}

type TSelection<Type> = {
    _id?: string;
    type: Type;
    attributeId: TCustomizationAttributeId; // attribute.id
    externalId: string;
    label: string; // attribute.name
    value?: undefined;
    valueId?: undefined;
    displayValue?: undefined;
    printType?: undefined;
    target?: undefined;
    imageFileName?: undefined;
    imageAttributes?: undefined;
    nestedValues?: undefined;
};

type TGenericSelection<Type, Extension> = Override<TSelection<Type>, Extension>;

type TGenericNestedSelection<Type, Extension> = TGenericSelection<Type, Override<{ valueKey: string }, Extension>>;

type Bindable = { target?: IBinding };
type Printable = { printType: string };

type TTextValue = {
    value: string; // user input text
    displayValue: string; // user input text
};

export type TCustomizationSelectionTextValue = TTextValue;

export type TCustomizationSelectionImageAttribute = {
    _id?: string;
    externalId: string;
    attributeId: string;
    name: string;
    value: string;
    isVisible: boolean;
    isApiFormattable: boolean;
};

type TImageValue = {
    value: TCustomizationImageId; // go customization image service - image id
    displayValue: TCustomizationImageUrl; // customization image service - image url
    merchantId?: string;
    imageFileName?: string;
    imageAttributes?: TCustomizationSelectionImageAttribute[];
};

export type TCustomizationSelectionImageValue = TImageValue;

type TColorPickerValue = {
    value: TRGBHex; // user input rgb hex
    displayValue: TRGBHex; // user input rgb hex
};

export type TCustomizationSelectionColorPickerValue = TColorPickerValue;

export type TSelectableValue = {
    valueId: TSelectableValueId; // selectable option id
    value: string; // value of selectable option
    displayValue: string; // name of selectable option
};

export type TCustomizationSelectionSelectableValue = TSelectableValue;

type TColorSwatchValue = {
    valueId: TSelectableValueId; // selectable option id
    value: TRGBHex; // rgb hex
    displayValue: string; // name of selectable option
};

export type TCustomizationSelectionColorSwatchValue = TColorSwatchValue;

export type TMetaTextSelection = TGenericNestedSelection<MetaSelectionType.TEXT, TTextValue>;

export type TMetaSelectableSelection = TGenericNestedSelection<
    MetaSelectionType.SELECTABLE,
    Bindable & TSelectableValue
>;

export type TMetaCustomizationSelection = TMetaTextSelection | TMetaSelectableSelection;

export type TPrintedTextChildAttributeSelection =
    | TPrintedTextOptionCustomizationSelection
    | TMetaCustomizationSelection;

type TPrintedTextOptionSelection<Type, ValueKey, Extension> = TGenericNestedSelection<
    Type,
    Override<{ valueKey: ValueKey }, Extension>
>;

export type TPrintedTextFontSelection = TPrintedTextOptionSelection<
    CustomizationSelectionType.SELECTABLE,
    PrintedTextOptionValueKey.FONT,
    TSelectableValue
>;

export type TPrintedTextFontSizeSelection = TPrintedTextOptionSelection<
    CustomizationSelectionType.SELECTABLE,
    PrintedTextOptionValueKey.FONT_SIZE,
    TSelectableValue
>;

export type TPrintedTextColorSwatchSelection = TPrintedTextOptionSelection<
    CustomizationSelectionType.COLOR_SWATCH,
    PrintedTextOptionValueKey.COLOR_SWATCH,
    TColorSwatchValue
>;

export type TPrintedTextColorPickerSelection = TPrintedTextOptionSelection<
    CustomizationSelectionType.COLOR_PICKER,
    PrintedTextOptionValueKey.COLOR_PICKER,
    TColorPickerValue
>;

export type TPrintedTextOptionCustomizationSelection =
    | TPrintedTextFontSelection
    | TPrintedTextFontSizeSelection
    | TPrintedTextColorSwatchSelection
    | TPrintedTextColorPickerSelection;

type TAttributeCustomizationSelection<Type, Extension> = TGenericSelection<Type, Bindable & Extension>;

export type TSelectableCustomizationSelection = TAttributeCustomizationSelection<
    CustomizationSelectionType.SELECTABLE,
    TSelectableValue
>;

export type TColorSwatchCustomizationSelection = TAttributeCustomizationSelection<
    CustomizationSelectionType.COLOR_SWATCH,
    TColorSwatchValue
>;

export type TColorPickerCustomizationSelection = TAttributeCustomizationSelection<
    CustomizationSelectionType.COLOR_PICKER,
    TColorPickerValue
>;

export type TPrintedTextCustomizationSelection = TAttributeCustomizationSelection<
    CustomizationSelectionType.PRINTED_TEXT,
    Printable &
        TTextValue & {
            nestedValues: TPrintedTextChildAttributeSelection[];
        }
>;

export type TPrintedImageCustomizationSelection = TAttributeCustomizationSelection<
    CustomizationSelectionType.PRINTED_IMAGE,
    Printable & TImageValue & { nestedValues: TMetaCustomizationSelection[] }
>;

export type TCustomizationSelection =
    | TSelectableCustomizationSelection
    | TColorSwatchCustomizationSelection
    | TColorPickerCustomizationSelection
    | TPrintedTextCustomizationSelection
    | TPrintedImageCustomizationSelection;

export type TNestedCustomizationSelection = TPrintedTextOptionCustomizationSelection | TMetaCustomizationSelection;
